<template>
  <div class="inventory-valuation-report">
    <v-card flat class="report-page-card">
      <ReportViewBar
        title="Inventory Valuation"
        :export-data="exportData"
        :export-fields="exportFields"
      />

      <v-card-text class="report-content">
        <v-card flat outlined class="report-table-card">
          <v-card-title class="report-table-header">
            <div>
              <div class="report-title">Inventory Valuation</div>
              <div class="report-subtitle">
                {{ Inventory.length }} categories found
              </div>
            </div>

            <v-spacer />

            <div class="summary-box">
              <div class="summary-label">Products</div>
              <div class="summary-value">{{ productCount }}</div>
            </div>

            <div class="summary-box">
              <div class="summary-label">Stock</div>
              <div class="summary-value">{{ grandTotal.balance }}</div>
            </div>

            <div class="summary-box">
              <div class="summary-label">Total Cost</div>
              <div class="summary-value">{{ grandTotal.total | currency }}</div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-0">
            <v-progress-linear v-if="isLoading" indeterminate height="3" />

            <v-data-table
              v-else
              dense
              sort-by="name"
              :mobile-breakpoint="0"
              :headers="headers"
              :single-expand="singleExpand"
              :expanded.sync="expanded"
              item-key="name"
              show-expand
              :items-per-page="-1"
              hide-default-footer
              :items="Inventory"
              class="inventory-table"
            >
              <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
                <v-btn
                  class="no-print"
                  icon
                  small
                  @click.stop="expand(!isExpanded)"
                >
                  <v-icon v-if="isExpanded">mdi-chevron-up</v-icon>
                  <v-icon v-else>mdi-chevron-down</v-icon>
                </v-btn>
              </template>

              <template v-slot:item.name="{ item }">
                <span class="category-name">
                  {{ item.name }}
                </span>
              </template>

              <template v-slot:item.productCount="{ item }">
                <span class="font-weight-bold">
                  {{ item.productCount }}
                </span>
              </template>

              <template v-slot:item.totalBalance="{ item }">
                <span class="font-weight-bold">
                  {{ item.totalBalance }}
                </span>
              </template>

              <template v-slot:item.totalCost="{ item }">
                <span class="money">
                  {{ item.totalCost | currency }}
                </span>
              </template>

              <template v-slot:body.append v-if="Inventory.length > 0">
                <tr class="total-row">
                  <td></td>

                  <td class="text-right font-weight-bold">
                    Total
                  </td>

                  <td class="text-left font-weight-bold">
                    {{ productCount }}
                  </td>

                  <td class="text-center font-weight-bold">
                    {{ grandTotal.balance }}
                  </td>

                  <td class="text-right font-weight-bold">
                    {{ grandTotal.total | currency }}
                  </td>
                </tr>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0">
                  <div class="expanded-panel">
                    <v-data-table
                      dense
                      sort-by="name"
                      :headers="productHeader"
                      hide-default-footer
                      :items-per-page="-1"
                      :items="item.value"
                      class="inventory-product-table"
                    >
                      <template v-slot:item.localId="{ item }">
                        <span class="font-weight-medium">
                          {{ item.localId }}
                        </span>
                      </template>

                      <template v-slot:item.name="{ item }">
                        <span class="product-name">
                          {{ item.name }}
                        </span>
                      </template>

                      <template v-slot:item.balance="{ item }">
                        <span
                          class="stock-link no-print"
                          @click.stop="updateBalanceProduct(item)"
                        >
                          {{ item.balance }}
                        </span>

                        <span class="print-stock">
                          {{ item.balance }}
                        </span>
                      </template>

                      <template v-slot:item.avgCost="{ item }">
                        <span class="money">
                          {{ getProductCost(item) | currency }}
                        </span>
                      </template>

                      <template v-slot:item.totalCost="{ item }">
                        <span class="money">
                          {{ item.totalCost | currency }}
                        </span>
                      </template>

                      <template v-slot:no-data>
                        <div class="empty-state">
                          No products found in this category.
                        </div>
                      </template>
                    </v-data-table>
                  </div>
                </td>
              </template>

              <template v-slot:no-data>
                <div class="empty-state">
                  No inventory found.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import reportService from "@/modules/ReportView/service.js";
import { mapGetters, mapActions } from "vuex";
import productService from "@/modules/Product/service.js";

export default {
  name: "inventory-valuation-report",

  components: {
    ReportViewBar,
  },

  data() {
    return {
      expanded: [],
      singleExpand: true,
      isLoading: true,
      Inventory: [],
      productHeader: [
        {
          text: "ID",
          align: "left",
          value: "localId",
          sortable: true,
        },
        {
          text: "Product Name",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Stock",
          align: "center",
          value: "balance",
          sortable: true,
        },
        {
          text: "Avg Cost",
          align: "right",
          value: "avgCost",
          sortable: true,
        },
        {
          text: "Total Cost",
          align: "right",
          value: "totalCost",
          sortable: true,
        },
      ],
      headers: [
        {
          text: "",
          value: "data-table-expand",
          width: 60,
          sortable: false,
        },
        {
          text: "Category",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Products",
          align: "left",
          value: "productCount",
          sortable: true,
        },
        {
          text: "Stock",
          align: "center",
          value: "totalBalance",
          sortable: true,
        },
        {
          text: "Total Cost",
          align: "right",
          value: "totalCost",
          sortable: true,
        },
      ],
      exportFields: {
        Category: "categoryName",
        "Product ID": "localId",
        "Product Name": "name",
        Stock: "balance",
        "Avg Cost": "avgCost",
        "Total Cost": "totalCost",
      },
    };
  },

  computed: {
    ...mapGetters("global", ["currentBusiness"]),

    grandTotal() {
      return this.Inventory.reduce(
        (totals, row) => {
          totals.total += Number(row.totalCost || 0);
          totals.balance += Number(row.totalBalance || 0);
          return totals;
        },
        {
          total: 0,
          balance: 0,
        }
      );
    },

    productCount() {
      return this.Inventory.reduce(
        (sum, row) => sum + Number(row.productCount || 0),
        0
      );
    },

    exportData() {
      const rows = [];

      this.Inventory.forEach((category) => {
        (category.value || []).forEach((product) => {
          rows.push({
            categoryName: category.name,
            localId: product.localId,
            name: product.name,
            balance: Number(product.balance || 0),
            avgCost: this.getProductCost(product),
            totalCost: Number(product.totalCost || 0),
          });
        });
      });

      return rows;
    },
  },

  created() {
    this.loadData();

    if (!this.currentBusiness) {
      this.initUser();
      this.initBusiness();
    }
  },

  methods: {
    ...mapActions("global", ["initUser", "initBusiness"]),

    getProductCost(product) {
      return product.avgCost && Number(product.avgCost) !== 0
        ? Number(product.avgCost || 0)
        : Number(product.cost || 0);
    },

    async updateBalanceProduct(product) {
      try {
        await productService.updateBalance(product.id);
        await this.loadData();
      } catch (err) {
        console.log(err);
      }
    },

    groupProductsByCategory(products) {
      const grouped = products.reduce((acc, product) => {
        const categoryName =
          product.Category && product.Category.name
            ? product.Category.name
            : "Uncategorized";

        if (!acc[categoryName]) {
          acc[categoryName] = {
            name: categoryName,
            value: [],
            totalBalance: 0,
            totalCost: 0,
            productCount: 0,
          };
        }

        acc[categoryName].value.push(product);
        acc[categoryName].totalBalance += Number(product.balance || 0);
        acc[categoryName].totalCost += Number(product.totalCost || 0);
        acc[categoryName].productCount += 1;

        return acc;
      }, {});

      return Object.values(grouped);
    },

    async loadData() {
      this.isLoading = true;

      try {
        const response = await reportService.getInventoryValuation();

        const products =
          response.data && response.data.rows ? response.data.rows : [];

        products.forEach((row) => {
          const costValue = this.getProductCost(row);
          row.totalCost = Number(row.balance || 0) * costValue;
        });

        this.Inventory = this.groupProductsByCategory(products);
        document.title = "Report - Inventory Valuation";
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.inventory-valuation-report {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 16px;
}

.report-page-card {
  border-radius: 18px;
  overflow: hidden;
}

.report-content {
  padding: 20px;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 120px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.category-name,
.product-name {
  font-weight: 600;
  text-transform: capitalize;
  color: #111827;
}

.money {
  font-weight: 700;
}

.stock-link {
  cursor: pointer;
  font-weight: 700;
  color: var(--v-primary-base);
}

.print-stock {
  display: none;
}

.expanded-panel {
  padding: 14px;
  background: #f3f8fc;
  border-top: 1px solid #e5e7eb;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.inventory-table ::v-deep th,
.inventory-product-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.inventory-table ::v-deep td,
.inventory-product-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .inventory-valuation-report {
    background: #fff;
    padding: 0;
  }

  .report-page-card,
  .report-table-card {
    box-shadow: none !important;
    border-radius: 0;
  }

  .report-content {
    padding: 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }

  .print-stock {
    display: inline;
  }
}
</style>